import React, { useEffect, useRef, useState } from "react";

/**
 * CountUp — short animated number tween for the hero card.
 * Starts at 0 and eases up to `value` over `duration` ms after `delay` ms.
 * Formats with thousands separators and a leading "$".
 *
 * Pure JS, no deps. Respects prefers-reduced-motion.
 */
export default function CountUp({
  value,
  duration = 900,
  delay = 0,
  prefix = "$",
  className = "",
  ...rest
}) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  const startedAt = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplay(value);
      return;
    }

    const start = () => {
      const tick = (now) => {
        if (!startedAt.current) startedAt.current = now;
        const elapsed = now - startedAt.current;
        const t = Math.min(1, elapsed / duration);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3);
        setDisplay(Math.round(value * eased));
        if (t < 1) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const timer = setTimeout(start, delay);
    return () => {
      clearTimeout(timer);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [value, duration, delay]);

  return (
    <span className={className} {...rest}>
      {prefix}
      {display.toLocaleString("en-US")}
    </span>
  );
}
